import { ReactNode } from 'react'
import { Skeleton, Stack, Typography, useTheme } from '@mui/material'
import { useGetCustomVariable } from '@/service-library/hooks/custom-variables'
import { useGetFlagTypes } from '@/service-library/hooks/flag-types'
import { useGetProjectTags } from '@/service-library/hooks/project-tags'
import { useGetWorkflowStateEvent } from '@/service-library/hooks/workflow-state-events'
import { WorkflowStateAction } from '@/types/workflow-state-actions'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import TagChip from '@/components/tags/TagChip'
import PrimaryTextWrapper from './PrimaryTextWrapper'

type QueueEventDisplayProps = {
  action: WorkflowStateAction
}

export default function QueueEventDisplay({ action }: QueueEventDisplayProps) {
  const theme = useTheme()
  const { fieldsMap } = useProjectContext()
  const { workflowStateEvent } = useGetWorkflowStateEvent({
    id: action.params.event_id,
    filters: {
      fields__only: 'id,name',
    },
  })

  const condition = action.params.condition

  const { customVariable } = useGetCustomVariable({
    id: action.params.org_custom_variable_id || '',
    enabled: condition === 'org_custom_variable_is',
  })

  const { projectTags, isPending: isLoadingProjectTags } = useGetProjectTags({
    filters: {
      id__in: action.params.project_tag_ids?.join(),
    },
    enabled: ['has_tag', 'does_not_have_specific_tag'].includes(condition),
  })

  const { flagTypes, isPending: isLoadingFlagTypes } = useGetFlagTypes({
    filters: {
      id__in: action.params.flag_type_ids?.join() || '',
    },
    enabled: [
      'has_specific_flag_type',
      'does_not_have_specific_flag_type',
    ].includes(condition),
  })

  if (!workflowStateEvent) return null

  const fieldId = action.params.field_id_with_flag
  const stringsToCheck = action.params.strings_to_check as Record<
    'value',
    string
  >[]

  const messageMap: Record<string, ReactNode> = {
    none: '.',
    has_flags: ' if the document has any flags.',
    has_no_flags: (
      <span>
        {' '}
        if the document <b>does not</b> have any flags.
      </span>
    ),
    // DEPRECATED
    has_flag_type: (
      <>
        <span>
          {' '}
          if the{' '}
          {action.params.field_id_with_flag ? (
            <>
              <PrimaryTextWrapper>
                {fieldsMap[fieldId]?.full_name}
              </PrimaryTextWrapper>{' '}
              field
            </>
          ) : (
            'document'
          )}{' '}
          has the following flag class.
        </span>
        <span style={{ color: theme.palette.warning.main, display: 'block' }}>
          Note: This condition is deprecated. Please use &quot;Has flag
          type&quot; instead.
        </span>{' '}
      </>
    ),
    // DEPRECATED
    does_not_have_flag_type: (
      <>
        <span>
          {' '}
          if the{' '}
          {action.params.field_id_with_flag ? (
            <>
              <PrimaryTextWrapper>
                {fieldsMap[fieldId]?.full_name}
              </PrimaryTextWrapper>{' '}
              field{' '}
            </>
          ) : (
            'document'
          )}{' '}
          <b>does not</b> have the following flag class:
        </span>
        <span style={{ color: theme.palette.warning.main, display: 'block' }}>
          Note: This condition is deprecated. Please use &quot;Does not have
          flag type&quot; instead.
        </span>{' '}
      </>
    ),
    has_specific_flag_type: (
      <span>
        {' '}
        if the{' '}
        {action.params.field_id_with_flag ? (
          <>
            <PrimaryTextWrapper>
              {fieldsMap[fieldId]?.full_name}
            </PrimaryTextWrapper>{' '}
            field
          </>
        ) : (
          'document'
        )}{' '}
        has the following flag type, regardless of whether it is a warning or an
        error:
      </span>
    ),
    does_not_have_specific_flag_type: (
      <span>
        {' '}
        if the{' '}
        {action.params.field_id_with_flag ? (
          <>
            <PrimaryTextWrapper>
              {fieldsMap[fieldId]?.full_name}
            </PrimaryTextWrapper>{' '}
            field
          </>
        ) : (
          'document'
        )}{' '}
        <b>does not</b> have the following flag type:
      </span>
    ),
    org_custom_variable_is: ` if the document's organization's custom variable matches the configured value:`,
    has_tag: ' if the document has the following tag:',
    does_not_have_specific_tag: (
      <span>
        {' '}
        if the document <b>does not</b> have the following tag:
      </span>
    ),
    has_any_tag: ' if the document has any tags.',
    has_no_tags: (
      <span>
        {' '}
        if the document <b>does not</b> have any tags.
      </span>
    ),
    contains_text: ' if the document contains the configured text:',
    check_checkbox_field: (
      <span>
        {' '}
        if the{' '}
        <PrimaryTextWrapper>
          {fieldsMap[action.params.checkbox_field_id]?.full_name}
        </PrimaryTextWrapper>{' '}
        field is <b>{action.params.checkbox_field_is}</b>.
      </span>
    ),
    field_is_populated: (
      <span>
        {' '}
        if the{' '}
        <PrimaryTextWrapper>
          {fieldsMap[action.params.populated_field_id]?.full_name}
        </PrimaryTextWrapper>{' '}
        field is populated.
      </span>
    ),
    field_is_not_populated: (
      <span>
        {' '}
        if the{' '}
        <PrimaryTextWrapper>
          {fieldsMap[action.params.populated_field_id]?.full_name}
        </PrimaryTextWrapper>{' '}
        field is <b>not</b> populated.
      </span>
    ),
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Queue the{' '}
        <PrimaryTextWrapper>{workflowStateEvent.name}</PrimaryTextWrapper> event
        to run after the current event
        {messageMap[condition]}
      </Typography>
      {/* // TODO: When we eventually switch this action to use actual flag types instead of relying on flag class,
    // make this show the actual flag. */}
      {(condition === 'has_flag_type' ||
        condition === 'does_not_have_flag_type') && (
        <Typography variant="body2">
          <b>Flag Class: </b>
          {action.params.flag_type_code}
        </Typography>
      )}
      {(condition === 'has_specific_flag_type' ||
        condition === 'does_not_have_specific_flag_type') && (
        <>
          <Typography variant="body2">
            <b>Flag Type{flagTypes.length > 1 ? 's' : ''}: </b>
          </Typography>
          {isLoadingFlagTypes ? (
            <Skeleton width={150} />
          ) : (
            flagTypes.map((flagType) => (
              <Typography
                key={flagType.id}
                variant="body2"
                color="text.secondary"
              >
                {flagType?.short_description}
              </Typography>
            ))
          )}
        </>
      )}
      {condition === 'org_custom_variable_is' && (
        <>
          <Typography variant="body2">
            <b>Custom Variable: </b>
            {customVariable?.name}
          </Typography>
          <Typography variant="body2">
            <b>Value: </b>
            {action.params.org_custom_variable_value}
          </Typography>
        </>
      )}
      {(condition === 'has_tag' ||
        condition === 'does_not_have_specific_tag') && (
        <Stack direction="row" gap={1} flexWrap="wrap">
          {isLoadingProjectTags ? (
            <Skeleton variant="rounded" width={50} height={20} />
          ) : (
            projectTags.map((projectTag) => (
              <TagChip key={projectTag.id} tag={projectTag} />
            ))
          )}
        </Stack>
      )}
      {condition === 'contains_text' && (
        <>
          <Typography variant="body2">
            <b>Text in Document:</b>
          </Typography>
          {stringsToCheck.map(({ value }, index) => (
            <Typography key={index} variant="body2" color="text.secondary">
              {value}
            </Typography>
          ))}
        </>
      )}
    </>
  )
}
